// Database verification of parsed precharting text (ICD-10 codes and medications).
// Shared by the Global Paste tab and the per-tab "Extract from Text" dialogs.
import { prechartingService } from '../apis/prechartingService';

const LOOKUP_CONCURRENCY = 4;

const normName = (s) => String(s || '').toLowerCase().replace(/\([^)]*\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();

// Guards against fuzzy DB matches that point to a different drug.
const namesAgree = (typed, dbName) => {
  if (!dbName) return true;
  const a = normName(typed);
  const b = normName(dbName);
  if (!a || !b) return true;
  return a.includes(b) || b.includes(a) || a.split(' ')[0] === b.split(' ')[0];
};

const runPool = async (tasks, limit) => {
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
};

/** Verifies ICD codes and medications against the database, mirroring the manual forms. */
export const enrichWithDatabase = async (parsed) => {
  const sections = { ...parsed.sections };
  const unrecognized = [...parsed.unrecognized];
  const tasks = [];

  if (sections.problemList) {
    const items = sections.problemList.items.map((p) => ({ ...p }));
    sections.problemList = { ...sections.problemList, items };
    items.forEach((p) => {
      tasks.push(async () => {
        try {
          const res = await prechartingService.lookupICD10Code(p.code);
          const desc = res?.success ? res.data?.description : '';
          if (desc) {
            p.description = desc;
            p.dbVerified = true;
          } else {
            p.dbVerified = false;
          }
        } catch (e) {
          p.dbVerified = false;
        }
      });
    });
  }

  if (sections.medicationList) {
    const items = sections.medicationList.items.map((m) => ({ ...m }));
    sections.medicationList = { ...sections.medicationList, items };
    items.forEach((m) => {
      tasks.push(async () => {
        let found = false;
        let data = null;
        let failed = false;
        try {
          const res = await prechartingService.lookupMedication((m.parsedName || m.name).trim());
          data = res?.success ? res.data : null;
          found = !!data && namesAgree(m.parsedName || m.name, data.medicationName);
          failed = !res?.success && !!res?.error && !/not found/i.test(String(res.message || ''));
        } catch (e) {
          failed = true;
        }
        if (found) {
          m.isValidated = true;
          m.isValid = true;
          m.allowUnvalidated = false;
          m.companyName = String(data.companyName || '').toUpperCase();
          m.medicationType = data.medicationType || '';
          m.preventive = data.preventive || false;
          m.dbVerified = true;
        } else {
          m.isValidated = !failed;
          m.isValid = false;
          m.allowUnvalidated = true;
          m.dbVerified = false;
        }
      });
    });
  }

  await runPool(tasks, LOOKUP_CONCURRENCY);

  // A name with no strength/sig that the drug database does not know is most likely not a drug at all.
  if (sections.medicationList) {
    const kept = [];
    sections.medicationList.items.forEach((m) => {
      if (m.bareName && !m.dbVerified) {
        unrecognized.push({
          section: 'medicationList',
          lineNumber: m.lineNumber,
          text: m.sourceLine || m.name,
          reason: 'Only a name was given and it was not found in the medication database, so it was not added',
        });
      } else {
        kept.push(m);
      }
    });
    sections.medicationList = { ...sections.medicationList, items: kept };
  }

  return { ...parsed, sections, unrecognized };
};
